import { useTranslation } from 'react-i18next'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from './ApiSearchableSelect'
import { useUsersSearch, type UseUsersSearchOptions } from '../hooks/useQueries'

interface UserSelectProps {
  org: string
  value: string
  onValueChange: (value: string) => void
  onOptionSelect?: (option: ApiSearchableSelectOption) => void
  userType?: UseUsersSearchOptions['userType']
  placeholder?: string
  initialOptions?: ApiSearchableSelectOption[]
  disabled?: boolean
  className?: string
  'data-testid'?: string
}

// Shared user picker backed by the API (useUsersSearch), not a client-side
// join against a truncated user list — see
// specs/todos/2026-07-21-04-user-pickers-truncated-to-first-page.md.
// minSearchLength is hardcoded to 0 so opening the picker immediately fires
// an empty-query search (first page, API's default order) instead of
// rendering blank until the user types.
export function UserSelect({
  org,
  value,
  onValueChange,
  onOptionSelect,
  userType,
  placeholder,
  initialOptions = [],
  disabled,
  className,
  'data-testid': dataTestId,
}: UserSelectProps) {
  const { t } = useTranslation('common')
  const { searchUsers } = useUsersSearch({ org, userType })

  return (
    <ApiSearchableSelect
      value={value}
      onValueChange={onValueChange}
      onOptionSelect={onOptionSelect}
      onSearch={searchUsers}
      initialOptions={initialOptions}
      minSearchLength={0}
      placeholder={placeholder ?? t('user_select.placeholder')}
      searchPlaceholder={t('user_select.search_placeholder')}
      emptyMessage={t('user_select.empty')}
      disabled={disabled}
      className={className}
      data-testid={dataTestId}
    />
  )
}
